<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useLoginStore } from "@/stores/login";
import { useUserStore } from "@/stores/user.ts";
import { useMessageStore } from "@/stores/message.ts";
import { getVisibleTabs } from "@/config/view-config";
import { useCurrentInstance } from "@/util";
import { PostLogout } from "@/apis/user";
import { useBreakpoint } from "@/composables/useBreakpoint";

// 移动端打开抽屉事件（由 App.vue 接管，渲染 MobileNavDrawer）
const emit = defineEmits<{
  (e: "open-mobile-nav"): void;
}>();

const loginStore = useLoginStore();
const userStore = useUserStore();
const messageStore = useMessageStore();
const { globalProperties } = useCurrentInstance();
const { isMobile } = useBreakpoint();

const isLoaded = ref(loginStore.isLoaded);

const isLogin = ref(false);

const username = ref(userStore.getUsername);
const nickname = ref(userStore.getNickname);

const tabList = ref();

const updateTabList = () => {
  // 用 getVisibleTabs 而不是 getGenerateTabs：「管理」这类容器 tab 自身不设门禁，
  // 入口是否显示由子页推导，否则普通用户会看到一个点开空无一物的「管理」。
  tabList.value = getVisibleTabs(userStore);
};

loginStore.$subscribe((_, state) => {
  isLoaded.value = state.Loaded;
});

userStore.$subscribe((_, state) => {
  username.value = state.username;
  nickname.value = state.nickname;
  isLogin.value = state.userId > 0;

  updateTabList();

  // Fetch unread count when user logs in
  if (state.userId > 0) {
    messageStore.fetchUnreadCount();
  } else {
    messageStore.clearUnread();
  }
});

// Poll unread count every 60s (raised from 30s — MessageView polls independently at 3s).
// Skips when MessageView is active or page is hidden to avoid redundant API calls.
const HEADER_POLL_INTERVAL = 60000;
let unreadPollTimer: ReturnType<typeof setInterval> | null = null;

const shouldPollUnread = () => {
  return !messageStore.isMessageViewActive && document.visibilityState !== "hidden";
};

const safeFetchUnread = () => {
  if (shouldPollUnread()) {
    messageStore.fetchUnreadCount();
  }
};

const startHeaderPoll = () => {
  stopHeaderPoll();
  messageStore.fetchUnreadCount();
  unreadPollTimer = setInterval(safeFetchUnread, HEADER_POLL_INTERVAL);
};

const stopHeaderPoll = () => {
  if (unreadPollTimer) {
    clearInterval(unreadPollTimer);
    unreadPollTimer = null;
  }
};

const onVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    // Catch up after returning to tab
    safeFetchUnread();
  }
};

watch(isLogin, (loggedIn) => {
  stopHeaderPoll();
  if (loggedIn) {
    startHeaderPoll();
    document.addEventListener("visibilitychange", onVisibilityChange);
  } else {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
});

const handleClickLogin = () => {
  if (globalProperties.$router.currentRoute.value.name !== "login") {
    globalProperties.$router.push({
      name: "login",
      query: { redirect_uri: globalProperties.$router.currentRoute.value.fullPath },
    });
  }
};

const handleClickLogout = async () => {
  try {
    await PostLogout()
    // Clear local store
    userStore.clear();
    messageStore.clearUnread();
    globalProperties.$message.success({
      duration: 3000,
      content: "注销成功",
    });
    globalProperties.$router.push({
      path: "/login",
      query: { redirect_uri: globalProperties.$router.currentRoute.value.fullPath },
    });
  } catch (error) {
    console.error("Logout failed:", error);
    // Clear local store anyway
    userStore.clear();
    messageStore.clearUnread();
    globalProperties.$message.success({
      duration: 3000,
      content: "注销成功",
    });
    globalProperties.$router.push({
      path: "/login",
      query: { redirect_uri: globalProperties.$router.currentRoute.value.fullPath },
    });
  }
};

onMounted(() => {
  updateTabList();
  if (isLogin.value) {
    startHeaderPoll();
    document.addEventListener("visibilitychange", onVisibilityChange);
  }
});

onUnmounted(() => {
  stopHeaderPoll();
  document.removeEventListener("visibilitychange", onVisibilityChange);
});
</script>

<template>
  <!-- 桌面端：原有 t-head-menu -->
  <t-head-menu
    v-if="!isMobile"
    v-model="$route.meta.tab"
    class="sh-menu"
    expand-type="popup"
  >
    <template #logo>
      <router-link to="/" class="sh-logo">DidaOJ</router-link>
    </template>
    <template v-for="tab in tabList" :key="tab.name">
      <t-menu-item :value="tab.name" :to="{ name: tab.name }">
        <template #icon>
          <t-icon v-if="tab.icon" :name="tab.icon" />
        </template>
        {{ tab.title }}
      </t-menu-item>
    </template>
    <template #operations>
      <t-submenu v-if="isLoaded && isLogin" :title="nickname">
        <template #icon>
          <t-badge :count="messageStore.unreadCount" :max-count="99" :offset="[-6, -2]">
            <t-icon name="user" />
          </t-badge>
        </template>
        <t-menu-item value="user_message" :to="{ name: 'message' }">
          <span class="msg-menu-item">
            <span>私信</span>
            <span v-if="messageStore.unreadCount > 0" class="msg-menu-badge">
              {{ messageStore.unreadCount > 99 ? '99+' : messageStore.unreadCount }}
            </span>
          </span>
        </t-menu-item>
        <t-menu-item value="user" :to="{ path: '/user/' + username }">个人空间</t-menu-item>
        <t-menu-item value="user_modify" :to="{ name: 'user-modify' }">用户设置</t-menu-item>
        <t-menu-item value="user_logout" @click="handleClickLogout"> 注销</t-menu-item>
      </t-submenu>
      <t-menu-item v-else-if="!isLoaded" value="login">
        <template #icon>
          <t-icon name="login" />
        </template>
        ...
      </t-menu-item>
      <t-menu-item v-else value="login" @click="handleClickLogin">
        <template #icon>
          <t-icon name="login" />
        </template>
        登录
      </t-menu-item>
    </template>
  </t-head-menu>

  <!-- 移动端：精简 header（汉堡 + logo + 用户图标） -->
  <div v-else class="sh-mobile-header">
    <t-button
      class="sh-mobile-menu-btn"
      variant="text"
      shape="square"
      @click="emit('open-mobile-nav')"
    >
      <t-icon name="view-list" size="24px" />
    </t-button>
    <router-link to="/" class="sh-mobile-logo">DidaOJ</router-link>
    <div class="sh-mobile-actions">
      <template v-if="isLoaded && isLogin">
        <t-badge :count="messageStore.unreadCount" :max-count="99" :offset="[-2, 2]">
          <t-button variant="text" shape="square" @click="handleClickLogin">
            <t-icon name="user" size="22px" />
          </t-button>
        </t-badge>
      </template>
      <template v-else-if="!isLoaded">
        <t-button variant="text" shape="square" disabled>
          <t-icon name="login" size="22px" />
        </t-button>
      </template>
      <template v-else>
        <t-button variant="text" shape="square" @click="handleClickLogin">
          <t-icon name="login" size="22px" />
        </t-button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.sh-menu {}

/* 私信菜单项 —— flex 布局避免 t-badge absolute 定位被裁切 */
.msg-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.msg-menu-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 8px;
  border-radius: 9px;
  font-size: 11px;
  line-height: 1;
  color: #fff;
  background: var(--td-error-color, #e34d59);
  flex-shrink: 0;
}

.sh-logo {
  margin-left: 64px;
  margin-top: 8px;
  margin-right: 32px;
  color: inherit;
  text-decoration: none;
  font-size: 18px;
  font-weight: bold;
  font-family: "Source Code Pro", monospace;

  &:hover {
    color: #1890ff;
  }
}

/* ============ 移动端 header ============ */
.sh-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 8px 0 4px;
  width: 100%;
  background: var(--td-bg-color-container, #fff);
}

.sh-mobile-menu-btn {
  flex-shrink: 0;
}

.sh-mobile-logo {
  color: inherit;
  text-decoration: none;
  font-size: 18px;
  font-weight: bold;
  font-family: "Source Code Pro", monospace;
  margin-left: 8px;

  &:hover {
    color: #1890ff;
  }
}

.sh-mobile-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
